/**
 * Self-Healing — Orphan Financial Records report (Feb 2026).
 *
 * Surfaces the 6 orphan buckets returned by /api/orphans/report
 * with three remediation actions per row:
 *   • 🔧 إصلاح تلقائي   — auto-create a Bank Adjustment (B1)
 *   • 🔗 ربط يدوي       — link to an existing journal (B2)
 *   • 🙈 تجاهل مع سبب   — record an ignore decision
 *
 * Mounted in the System Health / Bank tab. Admin-only actions are
 * gated client-side AND server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, Loader2, Link2, EyeOff, Sparkles,
  RefreshCw, X, ChevronDown, ChevronRight, BookOpen, Search,
} from 'lucide-react';
import { axiosInstance } from '../App';

const CATEGORY_LABELS = {
  bank_tx_no_journal:       'حركات بنكية بدون قيد',
  journal_no_bank_tx:       'قيود محاسبية بدون حركة بنكية',
  payment_no_bill:          'مدفوعات بدون فاتورة مورد',
  bill_no_payment:          'فواتير موردين بدون سداد',
  statement_line_unmatched: 'سطور كشف بنكي غير مطابقة',
  smart_engine_orphans:     'بقايا Smart Engine يتيمة',
};

const CATEGORY_ICONS = {
  bank_tx_no_journal:       '🏦',
  journal_no_bank_tx:       '📒',
  payment_no_bill:          '💸',
  bill_no_payment:          '🧾',
  statement_line_unmatched: '📑',
  smart_engine_orphans:     '🤖',
};

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const OrphanReport = ({ isAdmin = false }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedBucket, setExpandedBucket] = useState('bank_tx_no_journal');
  const [includeIgnored, setIncludeIgnored] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [activeSuggest, setActiveSuggest] = useState(null);
  // Self-Healing iteration #2 (Feb 2026):
  //   • full Journal Picker so the operator can browse ALL journals,
  //     not just the auto-suggested top-10.
  //   • Counter-account picker for auto-adjust of generic types (OTHER).
  const [browseTxId, setBrowseTxId] = useState(null);
  const [counterPicker, setCounterPicker] = useState(null); // {txId}

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/orphans/report?include_ignored=${includeIgnored}`,
      );
      setReport(data);
    } catch (e) {
      toast.error('تعذَّر تحميل تقرير اليتامى');
    } finally {
      setLoading(false);
    }
  }, [includeIgnored]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const runCleanup = async () => {
    if (!isAdmin) return;
    if (!window.confirm('سيتم حذف جميع روابط Smart Engine اليتيمة. متابعة؟')) return;
    try {
      const { data } = await axiosInstance.post('/bank/engine/cleanup-orphan-links?apply=true');
      toast.success(`تم حذف ${data.links_removed} رابط + ${data.matches_removed} match`);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التنظيف');
    }
  };

  const autoAdjust = async (txId, counterAccountCode = null) => {
    if (!isAdmin) return;
    if (!counterAccountCode) {
      if (!window.confirm('إنشاء Bank Adjustment تلقائياً وقيد محاسبي مرتبط؟')) return;
    }
    setActioningId(txId);
    try {
      const body = counterAccountCode
        ? { counter_account_code: counterAccountCode }
        : {};
      const { data } = await axiosInstance.post(
        `/orphans/bank-tx/${txId}/auto-adjust`,
        body,
      );
      toast.success(`تم إنشاء ${data.adjustment_no} وربطه بالحركة ✓`);
      setCounterPicker(null);
      setActiveSuggest(null);
      fetchReport();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // When backend needs a counter-account, open the picker instead
      // of dumping the error toast on the user.
      if (
        typeof detail === 'string' &&
        detail.includes('Counter Account')
      ) {
        setCounterPicker({ txId });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'فشل الإصلاح التلقائي');
    } finally {
      setActioningId(null);
    }
  };

  const openSuggest = async (txId) => {
    setActioningId(txId);
    try {
      const { data } = await axiosInstance.get(`/orphans/bank-tx/${txId}/suggest`);
      setActiveSuggest({ txId, ...data });
    } catch (e) {
      toast.error('تعذَّر تحميل الاقتراحات');
    } finally {
      setActioningId(null);
    }
  };

  const linkToJournal = async (txId, journalId) => {
    try {
      await axiosInstance.post(`/orphans/bank-tx/${txId}/link-journal`, {
        journal_id: journalId, note: 'manual link from Orphan Report',
      });
      toast.success('تم الربط ✓');
      setActiveSuggest(null);
      setBrowseTxId(null);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الربط');
    }
  };

  const ignoreOrphan = async (category, id) => {
    const reason = window.prompt('سبب تجاهل هذا السطر؟');
    if (!reason || reason.length < 3) return;
    try {
      await axiosInstance.post(`/orphans/${category}/${id}/ignore`, { reason });
      toast.success('تم التجاهل وتسجيل السبب');
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التجاهل');
    }
  };

  const summary = report?.summary || {};
  const overall = report?.overall || 'green';
  const overallColor = {
    green:  'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-amber-50 text-amber-700 border-amber-200',
    red:    'bg-rose-50 text-rose-700 border-rose-200',
  }[overall];
  const overallLabel = {
    green: 'النظام سليم — لا يتامى مالية', yellow: 'يتامى للمعالجة', red: 'يتامى حرجة تحتاج إصلاح',
  }[overall];

  return (
    <div className="space-y-4" data-testid="orphan-report">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-rose-500 rounded-xl flex items-center justify-center text-white">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">سجلات مالية يتيمة (Orphan Financial Records)</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Self-Healing — اكتشاف وإصلاح السجلات المالية غير المرتبطة محاسبياً
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 flex items-center gap-1">
              <input
                type="checkbox"
                checked={includeIgnored}
                onChange={(e) => setIncludeIgnored(e.target.checked)}
                data-testid="toggle-include-ignored"
              />
              عرض المتجاهَلة
            </label>
            <button
              onClick={fetchReport}
              disabled={loading}
              data-testid="refresh-orphan-report"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs border rounded-lg hover:bg-gray-50"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              تحديث
            </button>
            {isAdmin && (
              <button
                onClick={runCleanup}
                data-testid="cleanup-engine-orphans"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
              >
                🧹 تنظيف Smart Engine
              </button>
            )}
          </div>
        </div>

        <div className={`mt-4 p-3 rounded-lg border ${overallColor} flex items-center gap-2 text-sm`} data-testid="orphan-overall">
          {overall === 'green' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          <span className="font-medium">{overallLabel}</span>
          <span className="ml-auto text-xs">{summary.total || 0} سجل</span>
        </div>

        {/* KPI grid */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2">
          {Object.keys(CATEGORY_LABELS).map((k) => (
            <button
              key={k}
              onClick={() => setExpandedBucket(k)}
              data-testid={`orphan-bucket-kpi-${k}`}
              className={`text-right p-3 rounded-lg border transition ${
                expandedBucket === k
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
              }`}
            >
              <div className="text-lg">{CATEGORY_ICONS[k]}</div>
              <div className="text-[11px] text-gray-500 leading-tight mt-1">{CATEGORY_LABELS[k]}</div>
              <div className={`text-xl font-bold mt-1 ${
                (summary[k] || 0) > 0 ? 'text-rose-600' : 'text-gray-400'
              }`}>{summary[k] || 0}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Expanded bucket */}
      {report && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h4 className="font-medium text-gray-800 mb-3">
            {CATEGORY_ICONS[expandedBucket]} {CATEGORY_LABELS[expandedBucket]}
            <span className="text-gray-400 text-xs mr-2 ml-2">({(report.buckets[expandedBucket] || []).length})</span>
          </h4>
          {(report.buckets[expandedBucket] || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">لا يوجد سجلات في هذه الفئة 🎉</p>
          ) : (
            <OrphanBucketTable
              bucket={expandedBucket}
              rows={report.buckets[expandedBucket]}
              isAdmin={isAdmin}
              actioningId={actioningId}
              onAutoAdjust={autoAdjust}
              onSuggest={openSuggest}
              onIgnore={ignoreOrphan}
              onBrowseDirect={(id) => setBrowseTxId(id)}
            />
          )}
        </div>
      )}

      {/* B2 Suggestions modal */}
      {activeSuggest && (
        <SuggestModal
          data={activeSuggest}
          onClose={() => setActiveSuggest(null)}
          onLink={(jid) => linkToJournal(activeSuggest.txId, jid)}
          onAutoAdjust={() => { autoAdjust(activeSuggest.txId); }}
          onBrowseAll={() => {
            const id = activeSuggest.txId;
            setActiveSuggest(null);
            setBrowseTxId(id);
          }}
          isAdmin={isAdmin}
        />
      )}

      {/* Journal Picker — browse ALL journals (manual link) */}
      {browseTxId && (
        <JournalPickerModal
          txId={browseTxId}
          onClose={() => setBrowseTxId(null)}
          onPick={(jid) => linkToJournal(browseTxId, jid)}
        />
      )}

      {/* Counter-account picker — fallback when auto-adjust needs it */}
      {counterPicker && (
        <CounterAccountPickerModal
          txId={counterPicker.txId}
          onClose={() => setCounterPicker(null)}
          onPick={(code) => autoAdjust(counterPicker.txId, code)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Bucket table — columns vary by category
// ─────────────────────────────────────────────────────────────────
const OrphanBucketTable = ({ bucket, rows, isAdmin, actioningId, onAutoAdjust, onSuggest, onIgnore, onBrowseDirect }) => {
  const hasBankTxActions = bucket === 'bank_tx_no_journal';
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-gray-600">
          <tr>
            <th className="px-2 py-2 text-right">#</th>
            <th className="px-2 py-2 text-right">التاريخ</th>
            <th className="px-2 py-2 text-right">الوصف / المرجع</th>
            <th className="px-2 py-2 text-right">المبلغ</th>
            <th className="px-2 py-2 text-right">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`orphan-row-${bucket}-${r.id}`}>
              <td className="px-2 py-2 font-mono text-gray-500">
                {r.transaction_no || r.journal_no || r.payment_no || r.bill_no || r.line_no || r.id?.slice(0, 8)}
              </td>
              <td className="px-2 py-2">
                {r.transaction_date || r.journal_date || r.payment_date || r.bill_date || r.line_date || '—'}
              </td>
              <td className="px-2 py-2 text-gray-700">
                {r.description || r.reference || r.supplier_name || r.counterparty_name || '—'}
              </td>
              <td className="px-2 py-2 font-medium">
                {fmtMoney(r.amount || r.total_amount || r.balance_due)}
              </td>
              <td className="px-2 py-2">
                <div className="flex flex-wrap gap-1">
                  {hasBankTxActions && isAdmin && (
                    <>
                      <button
                        onClick={() => onBrowseDirect(r.id)}
                        disabled={actioningId === r.id}
                        data-testid={`orphan-browse-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                      >
                        <Link2 className="w-3 h-3" />
                        ربط يدوي
                      </button>
                      <button
                        onClick={() => onSuggest(r.id)}
                        disabled={actioningId === r.id}
                        data-testid={`orphan-suggest-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 disabled:opacity-50"
                      >
                        💡 اقتراحات
                      </button>
                      <button
                        onClick={() => onAutoAdjust(r.id)}
                        disabled={actioningId === r.id}
                        data-testid={`orphan-auto-adjust-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
                      >
                        <Sparkles className="w-3 h-3" />
                        إصلاح تلقائي
                      </button>
                    </>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => onIgnore(bucket, r.id)}
                      data-testid={`orphan-ignore-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100"
                    >
                      <EyeOff className="w-3 h-3" />
                      تجاهل
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Suggestion modal — Journals + Payments candidates with score
// ─────────────────────────────────────────────────────────────────
const SuggestModal = ({ data, onClose, onLink, onAutoAdjust, onBrowseAll, isAdmin }) => {
  const journals = data.journal_suggestions || [];
  const payments = data.payment_suggestions || [];
  const empty = !journals.length && !payments.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="orphan-suggest-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">اقتراحات الربط</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {data.transaction_no} · {fmtMoney(data.amount)} · {data.transaction_date}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-suggest-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {empty ? (
            <div className="text-center py-6 space-y-3">
              <p className="text-gray-500">
                لم يَتِم العُثور على قَيد أو دَفعة مُطابِقة (نَفس المَبلَغ، التاريخ ±3 أيام، البنك).
              </p>
              <p className="text-xs text-gray-400">
                يُمكِنك تَصَفُّح كل القيود المُحاسَبية يَدَوياً أو إنشاء Bank Adjustment جَديد.
              </p>
              <div className="flex flex-wrap gap-2 justify-center pt-2">
                <button
                  onClick={onBrowseAll}
                  data-testid="suggest-modal-browse-all"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
                >
                  <BookOpen className="w-4 h-4" />
                  تَصَفُّح القيود المُحاسَبية كامِلة
                </button>
                {isAdmin && (
                  <button
                    onClick={onAutoAdjust}
                    data-testid="suggest-modal-auto-adjust"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg"
                  >
                    <Sparkles className="w-4 h-4" />
                    أنشِئ Bank Adjustment تلقائياً
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {journals.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">قيود محاسبية مُحتَمَلة ({journals.length})</h4>
                  <div className="space-y-2">
                    {journals.map((j) => (
                      <div key={j.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-blue-50/30" data-testid={`suggest-journal-${j.id}`}>
                        <div>
                          <div className="font-mono text-xs text-blue-700">{j.journal_no}</div>
                          <div className="text-gray-700 mt-0.5">{j.description || '—'}</div>
                          <div className="text-[11px] text-gray-500 mt-1">
                            {j.journal_date} · {fmtMoney(j.total_amount)} · مرجع: {j.reference || '—'}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-xs font-bold ${
                            j.score >= 85 ? 'text-green-600' : j.score >= 70 ? 'text-amber-600' : 'text-gray-500'
                          }`}>{j.score}/100</span>
                          <button
                            onClick={() => onLink(j.id)}
                            data-testid={`link-journal-${j.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                          >
                            <Link2 className="w-3 h-3" />
                            رَبط
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {payments.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">دفعات مُحتَمَلة ({payments.length})</h4>
                  <div className="space-y-2">
                    {payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`suggest-payment-${p.id}`}>
                        <div>
                          <div className="font-mono text-xs text-purple-700">{p.payment_no}</div>
                          <div className="text-gray-700 mt-0.5">{p.supplier_name || '—'}</div>
                          <div className="text-[11px] text-gray-500 mt-1">
                            {p.payment_date} · {fmtMoney(p.amount)} · {p.reference || '—'}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs font-bold text-amber-600">{p.score}/100</span>
                          {p.journal_id && (
                            <button
                              onClick={() => onLink(p.journal_id)}
                              data-testid={`link-payment-${p.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                            >
                              <Link2 className="w-3 h-3" />
                              رَبط بالقيد
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t flex flex-wrap justify-center gap-2">
                <button
                  onClick={onBrowseAll}
                  data-testid="suggest-modal-browse-all"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
                >
                  <BookOpen className="w-4 h-4" />
                  تَصَفُّح كُل القيود المُحاسَبية
                </button>
                {isAdmin && (
                  <button
                    onClick={onAutoAdjust}
                    data-testid="suggest-modal-auto-adjust"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg"
                  >
                    <Sparkles className="w-4 h-4" />
                    أو أنشِئ Bank Adjustment جَديد تلقائياً
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────
// Journal Picker — browse ALL journals with search & link.
// Mounted when the operator clicks "تَصَفُّح القيود" in SuggestModal.
// ─────────────────────────────────────────────────────────────────
const JournalPickerModal = ({ txId, onClose, onPick }) => {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/journals');
        setJournals(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error('تعذَّر تحميل القيود');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return journals;
    return journals.filter((j) => {
      const hay = [
        j.journal_no, j.description, j.reference,
        j.journal_date, String(j.total_amount),
        j.source_type, j.source_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [journals, search]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="journal-picker-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">اختَر قَيداً مُحاسَبياً للرَبط</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              ابحَث في كُل القيود ثم اضغط «رَبط» بِجانِب القيد المُناسِب.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-journal-picker">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحَث برَقم القَيد، التاريخ، المَبلَغ، الوَصف، المَرجِع..."
              data-testid="journal-picker-search"
              className="w-full pr-9 pl-3 py-2 text-sm border rounded-lg"
            />
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            عُرِض {filtered.length} من أَصل {journals.length}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">جارٍ تحميل القيود...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">لا توجد نتائج مُطابِقة.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-right">رقم القَيد</th>
                  <th className="px-3 py-2 text-right">التاريخ</th>
                  <th className="px-3 py-2 text-right">الوَصف</th>
                  <th className="px-3 py-2 text-right">المَرجِع</th>
                  <th className="px-3 py-2 text-right">المَبلَغ</th>
                  <th className="px-3 py-2 text-right">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((j) => (
                  <tr key={j.id} className="border-t hover:bg-blue-50/30" data-testid={`journal-row-${j.journal_no || j.id}`}>
                    <td className="px-3 py-2 font-mono text-blue-700">{j.journal_no || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{fmtDate(j.journal_date)}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={j.description}>{j.description || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{j.reference || j.source_type || '—'}</td>
                    <td className="px-3 py-2 font-medium">{fmtMoney(j.total_amount)}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => onPick(j.id)}
                        data-testid={`picker-link-journal-${j.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                      >
                        <Link2 className="w-3 h-3" />
                        رَبط
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const fmtDate = (s) => (s ? String(s).split('T')[0] : '—');


// ─────────────────────────────────────────────────────────────────
// Counter-Account Picker — opens when auto-adjust needs an explicit
// counter account (e.g. transaction_type → OTHER, no heuristic).
// Filters the Chart of Accounts to expense + income + asset rows.
// ─────────────────────────────────────────────────────────────────
const CounterAccountPickerModal = ({ txId, onClose, onPick }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('expense');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/accounts');
        setAccounts(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error('تعذَّر تحميل دليل الحسابات');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (a.status && a.status !== 'active') return false;
      if (a.is_bank_account) return false;
      if (typeFilter && a.account_type !== typeFilter) return false;
      if (!q) return true;
      const hay = [a.account_number, a.account_name, a.name_arabic]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [accounts, search, typeFilter]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="counter-account-picker-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">اختَر الحِساب المُقابِل (Counter Account)</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              النظام يَحتاج اسم الحِساب المُقابِل لإنشاء القَيد المُحاسَبي (مَثَلاً: مَصروف بَنكي، شِراء أَدَوات...).
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-counter-picker">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b flex flex-wrap gap-2 items-center">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="counter-account-type-filter"
            className="px-3 py-2 text-sm border rounded-lg"
          >
            <option value="">كل الأنواع</option>
            <option value="expense">مَصروفات (Expense)</option>
            <option value="revenue">إيرادات (Revenue)</option>
            <option value="asset">أُصول (Asset)</option>
            <option value="liability">إلتِزامات (Liability)</option>
            <option value="equity">حُقوق المُلكِيَّة (Equity)</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحَث بالرَقم أو الاسم..."
              data-testid="counter-account-search"
              className="w-full pr-9 pl-3 py-2 text-sm border rounded-lg"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">جارٍ تحميل دليل الحسابات...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">لا توجد حسابات مُطابِقة.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-right">رقم الحِساب</th>
                  <th className="px-3 py-2 text-right">الاسم</th>
                  <th className="px-3 py-2 text-right">النَّوع</th>
                  <th className="px-3 py-2 text-right">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 400).map((a) => (
                  <tr key={a.id} className="border-t hover:bg-blue-50/30" data-testid={`counter-account-row-${a.account_number}`}>
                    <td className="px-3 py-2 font-mono text-blue-700">{a.account_number}</td>
                    <td className="px-3 py-2 text-gray-800">
                      {a.account_name}
                      {a.name_arabic && <span className="text-gray-500 text-[11px] mr-2">— {a.name_arabic}</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{a.account_type}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => onPick(a.account_number)}
                        data-testid={`pick-counter-account-${a.account_number}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg"
                      >
                        <Sparkles className="w-3 h-3" />
                        اختَر هذا الحِساب
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrphanReport;
